/**
 * AVOS module / feature-release registry — the ONE authoritative source of
 * truth for which customer-facing product modules exist and whether they are
 * released in a given environment.
 *
 * Why this module exists: development must be able to run every module, while
 * production exposes only the modules we have explicitly approved. Rather than
 * scattering `ENABLE_X` flags and feature checks across the sidebar, the router,
 * and the API server, this registry centralises:
 *
 *   - the module inventory (ids, names, route + API prefixes, nav grouping,
 *     cross-module dependencies);
 *   - the single environment flag `AVOS_ENABLED_MODULES` and its semantics;
 *   - pure resolver/validator helpers consumed identically by the frontend
 *     (navigation + route gating) and the backend (route + API gating).
 *
 * DESIGN BOUNDARIES (deliberate):
 *   - Only CUSTOMER-FACING PRODUCT MODULES live here. CORE PLATFORM SERVICES
 *     (authentication, sessions, tenant isolation/RLS, health/ready, the
 *     Channex webhook receiver, booking ingestion/ACK, ARI + availability-sync
 *     workers, reconciliation infrastructure, audit) are NEVER gated by a
 *     product-module flag — see {@link CORE_SERVICES} for the documented list.
 *   - API gating only lists API prefixes that are EXCLUSIVE to a single module.
 *     Shared prefixes (`/api/inventory`, `/api/booking`) are consumed by several
 *     modules (dashboard summaries, the global New Booking composer, CRM guest
 *     lookups) so gating them to one module would break the others. Those are
 *     intentionally NOT API-gated; the module's dedicated routes/nav are gated
 *     instead. This keeps gating correct and fail-safe.
 *   - This is a product-module layer. It is ORTHOGONAL to RBAC: a module flag
 *     controls AVAILABILITY only and never overrides authentication, session
 *     validation, RBAC, property access, or tenant isolation. Access requires
 *     module enabled AND authenticated AND permitted.
 *   - Future tenant-level entitlements can layer ON TOP of this (global
 *     availability AND tenant subscription AND user RBAC) without reworking the
 *     registry — resolvers take the enabled set as an argument, so a per-tenant
 *     set can be intersected later. No billing/subscription is built here.
 */

/** Stable product-module identifiers (the real console modules). */
export type ProductModuleId =
  | "dashboard"
  | "properties"
  | "bookings"
  | "channels"
  | "reconciliation"
  | "revenue"
  | "crm"
  | "automation"
  | "marketing"
  | "operations"
  | "agents"
  | "analytics"
  | "accounts"
  | "admin";

/** Metadata describing one customer-facing product module. */
export interface ModuleDefinition {
  /** Stable id (matches the console {@link ModuleKey}). */
  readonly id: ProductModuleId;
  /** Human-readable name. */
  readonly name: string;
  /**
   * Exact app route prefixes this module owns. A browser path equal to, or
   * nested under (`${prefix}/...`), any of these belongs to the module.
   */
  readonly routePrefixes: readonly string[];
  /**
   * API path prefixes EXCLUSIVE to this module (safe to gate server-side).
   * Empty when the module only consumes shared prefixes — see the module note.
   * Shared prefixes (/api/inventory, /api/booking) are intentionally omitted.
   */
  readonly apiPrefixes: readonly string[];
  /** Whether the module appears in the sidebar when enabled. */
  readonly nav: boolean;
  /**
   * Other product modules this module requires to function. Enabling a module
   * whose dependency is disabled is a configuration error (fail-closed at
   * startup). Discovered from real cross-module API usage.
   */
  readonly dependsOn: readonly ProductModuleId[];
  /**
   * `true` for modules that must ALWAYS be available regardless of the flag
   * (the shell cannot function without them). Only the dashboard + admin
   * (personal settings/sign-out) are always-on in the product layer.
   */
  readonly alwaysOn: boolean;
}

/**
 * THE registry. Keyed by id; array order is the canonical sidebar order. API
 * prefixes are only the EXCLUSIVE ones (see the file header for why shared
 * `/api/inventory` + `/api/booking` are omitted).
 */
export const MODULE_REGISTRY: Readonly<Record<ProductModuleId, ModuleDefinition>> = {
  dashboard: {
    id: "dashboard", name: "Dashboard",
    routePrefixes: ["/app/dashboard", "/app"],
    apiPrefixes: [], // reads cross-module summaries (/api/inventory, /api/reconciliation)
    nav: true, dependsOn: [], alwaysOn: true,
  },
  properties: {
    id: "properties", name: "Properties",
    // Properties OWNS the Inventory & Rates route too: Inventory & Rates is a
    // core property-management capability (units, rate plans, calendar,
    // restrictions, pricing, sync health) and is gated by the SAME `properties`
    // flag — it is NOT a separate product module and needs no extra env flag.
    routePrefixes: ["/app/properties", "/app/inventory"],
    apiPrefixes: [], // owns /api/inventory, but that is shared with dashboard/bookings/composer
    nav: true, dependsOn: [], alwaysOn: false,
  },
  bookings: {
    id: "bookings", name: "Bookings",
    routePrefixes: ["/app/bookings", "/app/booking"],
    apiPrefixes: [], // owns /api/booking, shared with the global New Booking composer + CRM
    nav: true, dependsOn: ["properties"], alwaysOn: false,
  },
  channels: {
    id: "channels", name: "Channel Manager",
    routePrefixes: ["/app/channels"],
    apiPrefixes: ["/api/channels/"],
    nav: true, dependsOn: ["properties"], alwaysOn: false,
  },
  reconciliation: {
    id: "reconciliation", name: "Reconciliation",
    routePrefixes: ["/app/reconciliation"],
    apiPrefixes: ["/api/reconciliation/"],
    nav: true, dependsOn: ["bookings"], alwaysOn: false,
  },
  revenue: {
    id: "revenue", name: "Revenue & Rates",
    routePrefixes: ["/app/revenue"],
    apiPrefixes: ["/api/revenue/"],
    nav: true, dependsOn: ["properties"], alwaysOn: false,
  },
  crm: {
    id: "crm", name: "Guest CRM",
    routePrefixes: ["/app/crm"],
    apiPrefixes: ["/api/crm/"],
    nav: true, dependsOn: [], alwaysOn: false,
  },
  automation: {
    id: "automation", name: "Automation",
    routePrefixes: ["/app/automation"],
    apiPrefixes: ["/api/automation/"],
    nav: true, dependsOn: [], alwaysOn: false,
  },
  marketing: {
    id: "marketing", name: "Marketing & SEO",
    routePrefixes: ["/app/marketing"],
    apiPrefixes: ["/api/marketing/"],
    nav: true, dependsOn: [], alwaysOn: false,
  },
  operations: {
    id: "operations", name: "Operations",
    routePrefixes: ["/app/operations"],
    apiPrefixes: ["/api/operations/"],
    nav: true, dependsOn: [], alwaysOn: false,
  },
  agents: {
    id: "agents", name: "AI Agents",
    routePrefixes: ["/app/agents"],
    apiPrefixes: ["/api/agent/"],
    nav: true, dependsOn: [], alwaysOn: false,
  },
  analytics: {
    id: "analytics", name: "Analytics",
    routePrefixes: ["/app/analytics"],
    apiPrefixes: ["/api/analytics/"],
    nav: true, dependsOn: [], alwaysOn: false,
  },
  accounts: {
    id: "accounts", name: "Accounts",
    routePrefixes: ["/app/accounts"],
    apiPrefixes: ["/api/accounts/"],
    nav: true, dependsOn: [], alwaysOn: false,
  },
  admin: {
    id: "admin", name: "Admin & Settings",
    routePrefixes: ["/app/settings"],
    apiPrefixes: [], // /api/admin + /api/access gate separately via RBAC; settings is personal + always-on
    nav: true, dependsOn: [], alwaysOn: true,
  },
};

/** All product-module ids in canonical order. */
export const ALL_MODULE_IDS: readonly ProductModuleId[] =
  Object.keys(MODULE_REGISTRY) as ProductModuleId[];

/**
 * CORE PLATFORM SERVICES — documented always-on infrastructure that is NEVER
 * gated by a product-module flag. Listed for the report + so reviewers can see
 * exactly what keeps running when a UI module is disabled. (Data only; the
 * actual routes live in the server. Public endpoints are additionally on the
 * auth-guard public allowlist.)
 */
export const CORE_SERVICES: readonly { readonly id: string; readonly note: string }[] = [
  { id: "authentication", note: "Login / logout / session / /api/auth/*" },
  { id: "sessions", note: "persistent_session, refresh rotation, cookie handling" },
  { id: "tenant-isolation", note: "withTenant + RLS + FORCE ROW LEVEL SECURITY" },
  { id: "health", note: "GET /health — liveness probe" },
  { id: "ready", note: "GET /ready — readiness probe (DB ping)" },
  { id: "channex-webhook", note: "POST /api/integrations/channex/webhooks — server-to-server, no browser session" },
  { id: "booking-ingestion", note: "OTA booking intake (webhook + poller), idempotent" },
  { id: "booking-ack", note: "Channex ACK of received bookings" },
  { id: "availability-sync", note: "ARI / availability sync workers" },
  { id: "reconciliation-infra", note: "Full Reconciliation background matching" },
  { id: "background-jobs", note: "worker.mjs sync processor + booking poller" },
  { id: "platform-admin", note: "Separate /api/platform-admin/* portal (its own auth)" },
  { id: "audit", note: "Audit/security event logging" },
];

// ---------------------------------------------------------------------------
// Configuration parsing + resolution
// ---------------------------------------------------------------------------

/** The sentinel meaning "every implemented product module is enabled". */
export const ALL_MODULES_TOKEN = "all" as const;

/** Outcome of parsing/validating the `AVOS_ENABLED_MODULES` configuration. */
export interface ModuleConfigResult {
  /** The resolved set of enabled module ids. */
  readonly enabled: ReadonlySet<ProductModuleId>;
  /** `true` when the raw value was the `all` sentinel. */
  readonly all: boolean;
  /** Configuration problems (unknown ids, missing dependencies). Non-empty ⇒ invalid. */
  readonly problems: readonly string[];
}

/**
 * Parse a raw `AVOS_ENABLED_MODULES` value into its tokens. `all` (any case,
 * trimmed) ⇒ the all-sentinel. A comma-separated list is normalised: trimmed,
 * lowercased, de-duplicated, empties dropped.
 */
function parseTokens(raw: string | undefined): { all: boolean; ids: string[] } {
  const value = (raw ?? "").trim();
  if (value === "") return { all: false, ids: [] };
  if (value.toLowerCase() === ALL_MODULES_TOKEN) return { all: true, ids: [] };
  const seen = new Set<string>();
  for (const part of value.split(",")) {
    const id = part.trim().toLowerCase();
    if (id !== "") seen.add(id);
  }
  return { all: false, ids: [...seen] };
}

/** Always-on module ids (resolved regardless of the flag). */
function alwaysOnIds(): ProductModuleId[] {
  return ALL_MODULE_IDS.filter((id) => MODULE_REGISTRY[id].alwaysOn);
}

/**
 * Resolve the enabled-module configuration from a raw flag value and the
 * environment. Pure + synchronous so the server (startup) and client can share
 * it. Fail-closed rules for hardened (production/staging) environments:
 *
 *   - `all` ⇒ every implemented module (convenient for development; also the
 *     preserved default for now so this change hides nothing).
 *   - explicit list ⇒ exactly those modules (+ always-on modules), with unknown
 *     ids reported as problems.
 *   - EMPTY/unset in a hardened env ⇒ a problem (never silently expose nothing
 *     AND never silently expose everything): the caller must set the flag
 *     explicitly. In development, empty defaults to `all` for convenience.
 *   - dependency validation: an enabled module whose dependency is disabled is
 *     a problem.
 *
 * Always-on modules (dashboard, admin) are force-included so the shell works.
 */
export function resolveModuleConfig(
  rawValue: string | undefined,
  options: { readonly hardened: boolean },
): ModuleConfigResult {
  const { all, ids } = parseTokens(rawValue);
  const problems: string[] = [];

  // Empty configuration handling.
  if (!all && ids.length === 0) {
    if (options.hardened) {
      problems.push(
        "AVOS_ENABLED_MODULES is not set. Set it to 'all' or an explicit comma-separated module list (fail-closed in production/staging).",
      );
      // Resolve to always-on only so nothing extra is exposed on this invalid config.
      return { enabled: new Set(alwaysOnIds()), all: false, problems };
    }
    // Development convenience: empty ⇒ all.
    return { enabled: new Set(ALL_MODULE_IDS), all: true, problems };
  }

  if (all) {
    return { enabled: new Set(ALL_MODULE_IDS), all: true, problems };
  }

  // Explicit list: validate ids.
  const known = new Set<ProductModuleId>();
  for (const id of ids) {
    if ((MODULE_REGISTRY as Record<string, ModuleDefinition>)[id] !== undefined) {
      known.add(id as ProductModuleId);
    } else {
      problems.push(`AVOS_ENABLED_MODULES contains an unknown module id: '${id}'.`);
    }
  }
  // Always-on modules are implicitly enabled.
  for (const id of alwaysOnIds()) known.add(id);

  // Dependency validation: every enabled module's dependencies must be enabled.
  for (const id of known) {
    for (const dep of MODULE_REGISTRY[id].dependsOn) {
      if (!known.has(dep)) {
        problems.push(
          `Module '${id}' requires '${dep}', but '${dep}' is not in AVOS_ENABLED_MODULES.`,
        );
      }
    }
  }

  return { enabled: known, all: false, problems };
}

/** Whether a given module id is enabled in a resolved set. */
export function isModuleEnabled(
  id: ProductModuleId,
  enabled: ReadonlySet<ProductModuleId>,
): boolean {
  return enabled.has(id);
}

/** Normalise a pathname: strip query/hash + trailing slash (except root). */
function normalize(pathname: string): string {
  const p = pathname.split(/[?#]/)[0] ?? "";
  if (p.length > 1 && p.endsWith("/")) return p.slice(0, -1);
  return p;
}

/** Does `pathname` equal, or nest under, `prefix`? */
function underPrefix(pathname: string, prefix: string): boolean {
  const p = normalize(pathname);
  const pre = normalize(prefix);
  return p === pre || p.startsWith(`${pre}/`);
}

/**
 * Resolve which product module owns an /app browser path, by longest matching
 * route prefix. `/app` (bare) and unknown /app paths resolve to `dashboard`.
 * Returns null for non-/app paths.
 */
export function moduleForAppPath(pathname: string): ProductModuleId | null {
  const p = normalize(pathname);
  if (p !== "/app" && !p.startsWith("/app/")) return null;
  let best: { id: ProductModuleId; len: number } | null = null;
  for (const id of ALL_MODULE_IDS) {
    for (const prefix of MODULE_REGISTRY[id].routePrefixes) {
      if (prefix === "/app") continue; // generic fallback, handled below
      if (underPrefix(p, prefix) && (best === null || prefix.length > best.len)) {
        best = { id, len: prefix.length };
      }
    }
  }
  return best?.id ?? "dashboard";
}

/**
 * Server-side APP ROUTE gate: is this browser /app path for a DISABLED module?
 * Returns the owning module id when gated, else null (allowed / not an app path
 * / always-on). The caller redirects a gated route to the dashboard.
 */
export function gatedAppRoute(
  pathname: string,
  enabled: ReadonlySet<ProductModuleId>,
): ProductModuleId | null {
  const id = moduleForAppPath(pathname);
  if (id === null) return null;
  if (MODULE_REGISTRY[id].alwaysOn) return null;
  return isModuleEnabled(id, enabled) ? null : id;
}

/**
 * Server-side API gate: is this /api path owned (exclusively) by a DISABLED
 * module? Returns the owning module id when gated, else null (allowed). Only
 * EXCLUSIVE prefixes are considered, so shared infrastructure is never gated.
 */
export function gatedApiPath(
  pathname: string,
  enabled: ReadonlySet<ProductModuleId>,
): ProductModuleId | null {
  const p = normalize(pathname);
  for (const id of ALL_MODULE_IDS) {
    const def = MODULE_REGISTRY[id];
    if (def.alwaysOn) continue;
    if (isModuleEnabled(id, enabled)) continue;
    for (const prefix of def.apiPrefixes) {
      if (p === normalize(prefix) || p.startsWith(prefix)) return id;
    }
  }
  return null;
}
